import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useQueueDelete } from '@/features/exceptions/hooks';

export function QueueDeleteDialog({
  queueId,
  queueIds,
  queueReference,
  queueReferences,
  onDeleted,
  size = 'sm',
}: {
  queueId?: string;
  queueIds?: readonly string[];
  queueReference?: string;
  queueReferences?: readonly string[];
  onDeleted?: () => void;
  size?: 'default' | 'sm' | 'lg' | 'icon';
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const deleteQueueItem = useQueueDelete();
  const ids = queueIds ?? (queueId ? [queueId] : []);
  const references = queueReferences ?? (queueReference ? [queueReference] : queueId ? [queueId] : []);
  const isBulk = ids.length > 1;
  const disabled = ids.length === 0 || deleteQueueItem.isPending;

  return (
    <>
      <Button variant="destructive" size={size} onClick={() => setOpen(true)} disabled={ids.length === 0}>
        <Trash2 className="h-4 w-4" />
        {isBulk ? 'Delete All' : 'Delete'}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isBulk ? 'Delete queue items' : 'Delete queue item'}</DialogTitle>
            <DialogDescription>
              {isBulk ? (
                <>
                  This permanently deletes <span className="font-medium text-foreground">{String(ids.length)} queue items</span>,
                  their execution history, and related records. This action cannot be undone.
                </>
              ) : (
                <>
                  This permanently deletes <span className="font-medium text-foreground">{references[0] ?? queueId}</span>,
                  its execution history, and related records. This action cannot be undone.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={deleteQueueItem.isPending}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                deleteQueueItem.mutate(ids, {
                  onSuccess: () => {
                    setOpen(false);
                    onDeleted?.();
                  },
                });
              }}
              disabled={disabled}
            >
              <Trash2 className="h-4 w-4" />
              {isBulk ? 'Permanently delete all' : 'Permanently delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
